import React, { useState, memo, useMemo } from "react";
import { Card, Button, Badge } from "react-bootstrap";
import { motion } from "framer-motion";

// Genre ID → Name Moview
const genreLookup = {
  28: "Action", 12: "Adventure", 16: "Animation", 35: "Comedy", 80: "Crime",
  99: "Documentary", 18: "Drama", 10751: "Family", 14: "Fantasy", 36: "History",
  27: "Horror", 10402: "Music", 9648: "Mystery", 10749: "Romance",
  878: "Science Fiction", 10770: "TV Movie", 53: "Thriller", 10752: "War", 37: "Western",
  10759: "Action & Adventure", 10765: "Sci-Fi & Fantasy", 10768: "War & Politics",
  10763: "News", 10764: "Reality", 10766: "Soap", 10767: "Talk"
};


const formatVotes = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

// Lazy loading image component
const LazyImage = memo(({ src, alt, className, style }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div className="image-container" style={{ position: 'relative', ...style }}>
      {!imageLoaded && !imageError && (
        <div 
          className="image-placeholder"
          style={{
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0
          }}
        >
          <span style={{ color: '#999' }}>Loading...</span>
        </div>
      )}
      {imageError && (
        <div 
          className="image-error"
          style={{
            backgroundColor: '#f8f8f8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0
          }}
        >
          <span style={{ color: '#999' }}>No Image</span>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
        style={{
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          ...style
        }}
      />
    </div>
  );
});


const MovieCard = memo(({ movie, handleShowTrailer, selectedProvider }) => {
  const [expanded, setExpanded] = useState(false);
  const toggleOverview = () => setExpanded(!expanded);

  // Memoize expensive calculations
  const genreNames = useMemo(() => 
    movie.genre_ids.map((id) => genreLookup[id] || `Genre ${id}`),
    [movie.genre_ids]
  );
  
  const truncatedOverview = useMemo(() =>
    movie.overview.length > 200 ? movie.overview.slice(0, 200) + "..." : movie.overview,
    [movie.overview]
  );
  
  const releaseYear = useMemo(() => 
    new Date(movie.release_date).getFullYear(),
    [movie.release_date]
  );
  
  const formattedVotes = useMemo(() =>
    formatVotes(movie.vote_count),
    [movie.vote_count]
  );

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="h-100 cinematic-panel-compact movie-card-cinematic">
        {movie.poster_path && (
          <div className="poster-wrapper">
            <LazyImage
              src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
              alt={`${movie.title} Poster`}
              className="poster-img"
            />
          </div>
        )}
        <Card.Body className="panel-content-compact">
          <Card.Title>
            {movie.title} ({releaseYear})
          </Card.Title>

          <div className="d-flex align-items-center mb-2">
            <h5 className="mb-0 me-3">
              <span role="img" aria-label="star">⭐</span> {movie.vote_average.toFixed(1)}
            </h5>
            <Badge bg="secondary">{formattedVotes} votes</Badge>
          </div>

          <div className="mb-2 flex-wrap d-flex gap-1">
            {genreNames.map((genre, i) => (
              <Badge key={i} bg="info" className="me-1">
                {genre}
              </Badge>
            ))}
          </div>

          {movie.cast && movie.cast.length > 0 && (
            <div className="mb-2 movie-card-meta-line">
              <strong>Cast:</strong>{" "}
              <span>
                {movie.cast.slice(0, 5).join(", ")}
                {movie.cast.length > 5 && "…"}
              </span>
            </div>
          )}

          <div className="mb-3">
            <strong>Summary:</strong>
            <p className="movie-card-summary">
              {expanded ? movie.overview : truncatedOverview}
              {movie.overview.length > 200 && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={toggleOverview}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleOverview();
                    }
                  }}
                  className="movie-card-read-more"
                >
                  {expanded ? "Show less ▲" : "Read more ▼"}
                </span>
              )}
            </p>
          </div>

          {movie.trailer && (
            <div className="text-center my-3">
              <Button
                variant="primary"
                onClick={() => handleShowTrailer(movie.trailer)}
                className="px-4 py-2 trailer-button"
                style={{ fontWeight: 500, borderRadius: "30px" }}
              >
                <span role="img" aria-label="star">🎬</span> Watch Trailer
              </Button>
            </div>
          )}

          {movie.providers && (
            <>
              <hr />
              <div className="mt-3">
                {(() => {
                  // Determine which provider types to show based on selection
                  const isAllProviders = !selectedProvider || selectedProvider === "" || selectedProvider === "Any";
                  const typesToShow = isAllProviders ? ["streaming", "rent", "buy"] : ["streaming"];
                  
                  return typesToShow.map((type) => {
                    let providers = movie.providers[type] || [];
                    
                    // Filter providers if a specific provider is selected
                    if (!isAllProviders && providers.length > 0) {
                      providers = providers.filter(provider => {
                        // Create a mapping of FilterBar provider IDs to TMDB provider names
                        const providerMapping = {
                          "8": "Netflix",
                          "337": "Disney Plus", 
                          "15": "Hulu",
                          "9": "Amazon Prime Video",
                          "1899": "HBO Max", // TMDB provider id 1899; API may still list provider_name as Max or HBO Max
                          "350": "Apple TV",
                          "531": "Paramount+",
                          "386": "Peacock",
                          "283": "Crunchyroll",
                          "524": "Discovery+",
                          "37": "Showtime",
                          "43": "Starz"
                        };
                        
                        const expectedProviderName = providerMapping[selectedProvider];
                        if (!expectedProviderName) return false;
                        
                        // Check if provider name matches (case insensitive, handles variations)
                        const providerName = provider.provider_name;
                        const lowerProviderName = providerName.toLowerCase();
                        const lowerExpectedName = expectedProviderName.toLowerCase();
                        
                        // Handle exact matches and common variations
                        return lowerProviderName === lowerExpectedName || 
                               lowerProviderName.includes(lowerExpectedName) ||
                               lowerExpectedName.includes(lowerProviderName) ||
                               // Handle specific provider variations
                               (expectedProviderName === "Disney Plus" && (lowerProviderName.includes("disney") && lowerProviderName.includes("plus"))) ||
                               (expectedProviderName === "Apple TV" && lowerProviderName.includes("apple tv")) ||
                               (expectedProviderName === "Amazon Prime Video" && lowerProviderName.includes("amazon") && lowerProviderName.includes("prime")) ||
                               (expectedProviderName === "HBO Max" && (lowerProviderName === "max" || lowerProviderName.includes("hbo max")));
                      });
                    }
                    
                    if (providers.length === 0) return null;

                    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
                    return (
                      <div key={type} className="mb-3">
                        <div className="movie-card-provider-type-chip">
                          {typeLabel.toUpperCase()}
                        </div>

                        {/* ✅ Horizontal scrollable logo row */}
                        <div className="provider-scroll-wrapper">
                          {providers.map((provider, i) => {
                            const providerHomepages = {
                              // Netflix
                              "Netflix": "https://www.netflix.com",
                              "Netflix basic with Ads": "https://www.netflix.com",
                              
                              // Amazon Prime Video
                              "Amazon Prime Video": "https://www.primevideo.com",
                              "Amazon Prime Video with Ads": "https://www.primevideo.com",
                              "Amazon Video": "https://www.primevideo.com",
                              
                              // Disney+
                              "Disney Plus": "https://www.disneyplus.com",
                              "Disney+": "https://www.disneyplus.com",
                              
                              // HBO Max (TMDB / logos may still use "Max")
                              "Max": "https://www.max.com",
                              "HBO Max": "https://www.max.com",
                              
                              // Hulu
                              "Hulu": "https://www.hulu.com",
                              
                              // Apple TV+
                              "Apple TV": "https://tv.apple.com",
                              "Apple TV Plus": "https://tv.apple.com",
                              "Apple TV+": "https://tv.apple.com",
                              
                              // Paramount+
                              "Paramount+": "https://www.paramountplus.com",
                              "Paramount Plus": "https://www.paramountplus.com",
                              
                              // Peacock
                              "Peacock": "https://www.peacocktv.com",
                              "Peacock Premium": "https://www.peacocktv.com",
                              "Peacock Premium Plus": "https://www.peacocktv.com",
                              
                              // Starz
                              "Starz": "https://www.starz.com",
                              
                              // Showtime
                              "Showtime": "https://www.showtime.com",
                              "Showtime Amazon Channel": "https://www.amazon.com/showtime",
                              
                              // Crunchyroll
                              "Crunchyroll": "https://www.crunchyroll.com",
                              
                              // Discovery+
                              "Discovery+": "https://www.discoveryplus.com",
                              "Discovery Plus": "https://www.discoveryplus.com",
                              
                              // YouTube
                              "YouTube": "https://youtube.com",
                              "YouTube Premium": "https://youtube.com/premium",
                              
                              // Pluto TV
                              "Pluto TV": "https://pluto.tv",
                              
                              // Tubi
                              "Tubi": "https://tubitv.com",
                              
                              // Roku Channel
                              "The Roku Channel": "https://therokuchannel.roku.com",
                              "Roku Channel": "https://therokuchannel.roku.com",
                              
                              // FuboTV
                              "fuboTV": "https://www.fubo.tv",
                              "Fubo TV": "https://www.fubo.tv",
                              
                              // Sling TV
                              "Sling TV": "https://www.sling.com",
                              
                              // Philo
                              "Philo": "https://philo.com",
                              
                              // DIRECTV STREAM
                              "DIRECTV": "https://www.directv.com/stream/",
                              
                              // Vudu
                              "Vudu": "https://www.vudu.com",
                              "Vudu Free": "https://www.vudu.com",
                              
                              // Google Play Movies & TV
                              "Google Play Movies": "https://play.google.com/store/movies",
                              "Google Play Movies & TV": "https://play.google.com/store/movies",
                              
                              // Microsoft Store
                              "Microsoft Store": "https://www.microsoft.com/en-us/store/movies-and-tv",
                              
                              // Redbox
                              "Redbox": "https://www.redbox.com",
                              
                              // FandangoNOW (now Vudu)
                              "FandangoNOW": "https://www.vudu.com",
                              
                              // Plex
                              "Plex": "https://www.plex.tv",
                              "Plex Player": "https://www.plex.tv",
                              
                              // IMDB TV (now Freevee)
                              "IMDB TV": "https://www.amazon.com/adlp/imdbfree",
                              "IMDb TV": "https://www.amazon.com/adlp/imdbfree",
                              "Freevee": "https://www.amazon.com/adlp/freevee",
                              
                              // Hoopla
                              "Hoopla": "https://www.hoopladigital.com",
                              
                              // Kanopy
                              "Kanopy": "https://www.kanopy.com",
                              
                              // Crackle
                              "Crackle": "https://www.crackle.com",
                              "Sony Crackle": "https://www.crackle.com",
                              
                              // AMC+
                              "AMC+": "https://www.amcplus.com",
                              "AMC Plus": "https://www.amcplus.com",
                              
                              // BritBox
                              "BritBox": "https://www.britbox.com",
                              
                              // Shudder
                              "Shudder": "https://www.shudder.com",
                              
                              // Acorn TV
                              "Acorn TV": "https://acorn.tv",
                              
                              // Hallmark Movies Now (now Hallmark Movies & Mysteries)
                              "Hallmark Movies Now": "https://www.hallmarkmoviesandmysteries.com",
                              
                              // ESPN+
                              "ESPN Plus": "https://plus.espn.com",
                              "ESPN+": "https://plus.espn.com",
                              
                              // Turkish Services
                              "BluTV": "https://www.blutv.com",
                              "Exxen": "https://www.exxen.com",
                              "BeIN CONNECT": "https://connect.bein.com.tr",
                              
                              // Polish Services
                              "Canal+": "https://www.canalplus.com",
                              "Player": "https://player.pl",
                              "CDA Premium": "https://www.cda.pl",
                              
                              // Portuguese Services
                              "RTP Play": "https://www.rtp.pt/play",
                              "MEO": "https://www.meo.pt",
                              "SIC Caras": "https://siccaras.pt",
                              
                              // Finnish Services
                              "MTV Katsomo": "https://www.mtv.fi/katsomo",
                              "Ruutu": "https://www.ruutu.fi",
                              "C More": "https://www.cmore.fi",
                              
                              // Danish Services
                              "TV 2 Play": "https://play.tv2.dk",
                              "Viaplay": "https://viaplay.dk",
                              "DR TV": "https://www.dr.dk/drtv"
                            };

                            // Get homepage URL, with fallback for unknown providers
                            let homepage = providerHomepages[provider.provider_name];
                            
                            // Fallback: Try to construct a search URL if no direct homepage exists
                            if (!homepage) {
                              const searchTerm = encodeURIComponent(provider.provider_name);
                              homepage = `https://www.google.com/search?q=${searchTerm}+streaming+service`;
                            }
                            
                            const logoUrl = `https://image.tmdb.org/t/p/w92${provider.logo_path}`;

                            // All logos are now clickable with proper mobile/desktop handling
                            return (
                              <a
                                key={i}
                                href={homepage}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Go to ${provider.provider_name}`}
                                className="provider-logo-link clickable"
                                style={{
                                  display: 'inline-block',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  // Enhanced mobile touch target
                                  minWidth: '44px',
                                  minHeight: '44px',
                                  // Improved touch feedback
                                  WebkitTapHighlightColor: 'rgba(102, 126, 234, 0.2)',
                                  // Ensure proper stacking
                                  position: 'relative',
                                  zIndex: 1
                                }}
                                onTouchStart={(e) => {
                                  // Add immediate visual feedback on touch
                                  e.currentTarget.style.transform = 'scale(0.95)';
                                }}
                                onTouchEnd={(e) => {
                                  // Reset scale after touch
                                  setTimeout(() => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }, 150);
                                }}
                                onClick={(e) => {
                                  // Ensure click works on all devices
                                  e.stopPropagation();
                                }}
                              >
                                <div className="provider-logo-wrapper">
                                  <img
                                    src={logoUrl}
                                    alt={provider.provider_name}
                                    className="provider-logo"
                                    style={{
                                      display: 'block',
                                      maxWidth: '100%',
                                      height: 'auto',
                                      // Prevent image drag interfering with clicks
                                      userSelect: 'none',
                                      WebkitUserSelect: 'none',
                                      MozUserSelect: 'none',
                                      msUserSelect: 'none',
                                      pointerEvents: 'none' // Let parent handle clicks
                                    }}
                                  />
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
});

export default MovieCard;
